import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, FileText, Video, BookOpen, Eye, EyeOff, Compass, Star } from "lucide-react";
import { AdminReadingPaths } from "./AdminReadingPaths";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_TABS = [
  { key: "document", label: "Documentos", Icon: FileText },
  { key: "video", label: "Vídeos", Icon: Video },
  { key: "itinerary", label: "Itinerários", Icon: BookOpen },
  { key: "paths", label: "Percursos de leitura", Icon: Compass },
];

const EMPTY = (kind) => ({
  kind, title: "", author: "", category: "", description: "", tags: "", url: "", body: "",
  language: "pt", order: 0, featured_start: false, published: true,
});

export default function AdminLibrary() {
  const [kind, setKind] = useState("document");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({ thematic: [], institutional: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | EMPTY | item

  const load = useCallback(async () => {
    if (kind === "paths") return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/library?kind=${kind}`, { withCredentials: true });
      setItems(r.data.items || []);
      setCategories({
        thematic: r.data.categories?.thematic || [],
        institutional: r.data.categories?.institutional || [],
      });
    } catch (e) { toast.error("Erro a carregar."); }
    finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const allCats = [...categories.thematic, ...categories.institutional];
  const catLabelFor = (slug) => allCats.find((c) => c.slug === slug)?.label || slug;

  const startCreate = () => setEditing(EMPTY(kind));
  const startEdit = (it) => setEditing({ ...it, tags: (it.tags || []).join(", ") });
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e?.preventDefault?.();
    const tags = String(editing.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
    const payload = { ...editing, tags, order: Number(editing.order) || 0 };
    try {
      if (editing.id) {
        await axios.patch(`${API}/admin/library/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/library`, payload, { withCredentials: true });
      }
      toast.success("Guardado.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Apagar "${it.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/library/${it.id}`, { withCredentials: true });
      toast.success("Apagado.");
      load();
    } catch (e) { toast.error("Erro."); }
  };

  const togglePublished = async (it) => {
    try {
      await axios.patch(`${API}/admin/library/${it.id}`, { published: !it.published }, { withCredentials: true });
      load();
    } catch (e) { toast.error("Erro."); }
  };

  return (
    <div className="px-6 py-10 lg:px-12 lg:py-14" data-testid="admin-library-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Biblioteca</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Conteúdos para sócios.</h1>
          <span className="accent-rule mt-6 block" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-xl">
            Uma biblioteca para conhecer, discernir e transformar a vida. Apenas os itens publicados aparecem na área de sócios.
          </p>
        </div>
        {kind !== "paths" && (
          <button onClick={startCreate} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630] transition-colors" data-testid="library-create-button">
            <Plus className="h-4 w-4" /> Adicionar
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-10 flex flex-wrap gap-1 border-b border-[hsl(var(--border))]">
        {KIND_TABS.map((t) => (
          <button
            key={t.key} onClick={() => { setKind(t.key); setEditing(null); }}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${
              kind === t.key
                ? "border-[hsl(var(--accent))] text-[hsl(var(--primary))]"
                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            }`}
            data-testid={`library-tab-${t.key}`}
          >
            <t.Icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {kind === "paths" ? <AdminReadingPaths /> : (<>
      {/* Editing form */}
      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-[hsl(var(--border))] p-6 sm:p-8 space-y-5" data-testid="library-edit-form">
          <div className="flex items-center justify-between">
            <h2 className="display-heading text-2xl">{editing.id ? "Editar item" : "Novo item"}</h2>
            <span className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--accent))]">
              {KIND_TABS.find((k) => k.key === editing.kind)?.label}
            </span>
          </div>

          <Field label="Título" required>
            <input
              type="text" value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              required className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="lib-title"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Autor (opcional)">
              <input
                type="text" value={editing.author || ""}
                onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                placeholder="ex.: Pe. José Kentenich"
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="lib-author"
              />
            </Field>
            <Field label="Categoria">
              <select
                value={editing.category || ""}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="lib-category"
              >
                <option value="">— sem categoria —</option>
                <optgroup label="Temáticas">
                  {categories.thematic.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Arquivo Institucional">
                  {categories.institutional.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </optgroup>
              </select>
            </Field>
          </div>

          {(editing.kind === "document" || editing.kind === "video") && (
            <Field label={`URL ${editing.kind === "video" ? "(YouTube ou Vimeo)" : "(link público, ex.: Google Drive)"}`} required>
              <input
                type="url" value={editing.url || ""}
                onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                required placeholder={editing.kind === "video" ? "https://youtu.be/..." : "https://..."}
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="lib-url"
              />
            </Field>
          )}

          <Field label="Descrição (opcional)">
            <textarea value={editing.description || ""}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2}
              className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="lib-description"
            />
          </Field>

          <Field label="Palavras-chave (opcional)" hint="Separadas por vírgulas — usadas na pesquisa. Ex.: aliança de amor, pedagogia, confiança">
            <input
              type="text" value={editing.tags || ""}
              onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
              className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="lib-tags"
            />
          </Field>

          {editing.kind === "itinerary" && (
            <Field label="Corpo do itinerário" required hint="Use linhas em branco para separar parágrafos.">
              <textarea value={editing.body || ""}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={12}
                required
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="lib-body"
              />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Ordem">
              <input type="number" value={editing.order ?? 0}
                onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="lib-order"
              />
            </Field>
            <Field label="Idioma">
              <select value={editing.language || "pt"}
                onChange={(e) => setEditing({ ...editing, language: e.target.value })}
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="lib-language"
              >
                <option value="pt">Português</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </Field>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!editing.featured_start}
                onChange={(e) => setEditing({ ...editing, featured_start: e.target.checked })}
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                data-testid="lib-featured"
              />
              <span className="text-sm inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Destacar em "Começar por aqui"</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!editing.published}
                onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                data-testid="lib-published"
              />
              <span className="text-sm">Publicar (visível na área de sócios)</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[hsl(var(--border))]">
            <button type="button" onClick={cancel} className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="lib-cancel">
              Cancelar
            </button>
            <button type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630]" data-testid="lib-save">
              Guardar
            </button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="mt-8">
        {loading ? (
          <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] p-12 text-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Ainda sem itens deste tipo.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="library-list">
            {items.map((it) => {
              const catLabel = catLabelFor(it.category);
              return (
                <li key={it.id} className={`bg-white border border-[hsl(var(--border))] p-4 flex items-start justify-between gap-3 ${!it.published ? "opacity-60" : ""}`} data-testid={`library-row-${it.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-[hsl(var(--foreground))] truncate">{it.title}</h3>
                      {it.featured_start && <Star className="h-3.5 w-3.5 text-[hsl(var(--accent))] shrink-0" title="Começar por aqui" />}
                      {!it.published && <span className="text-[10px] uppercase tracking-wider bg-[hsl(var(--muted))] px-1.5 py-0.5">Rascunho</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[hsl(var(--muted-foreground))]">
                      {it.author && <span>{it.author}</span>}
                      {catLabel && <span>{catLabel}</span>}
                      <span>Ordem: {it.order}</span>
                      <span>{it.language?.toUpperCase()}</span>
                      {it.url && (
                        <a href={it.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]">
                          <ExternalLink className="h-3 w-3" /> abrir
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 shrink-0">
                    <button onClick={() => togglePublished(it)} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" title={it.published ? "Despublicar" : "Publicar"} data-testid={`library-toggle-${it.id}`}>
                      {it.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button onClick={() => startEdit(it)} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`library-edit-${it.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(it)} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid={`library-delete-${it.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </>)}
    </div>
  );
}

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">
      {label}{required ? " *" : ""}
    </label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</p>}
  </div>
);
